import { formatDate } from './dates.ts';

/** A site visit deadline as the words below read it: the fields of PROPERTY_SITE_VISIT they use. */
export interface SiteVisitRow {
  status: string;
  met_by_property_visit_id: string | null;
  property_left_on: string | null;
}

/**
 * What THIS visit met, in words, or null if it met nothing (F-149): only a Complete row the server says this visit met
 * (met_by_property_visit_id). A property can hold several rows (178), so another row being Complete says nothing about
 * this visit: a second visit, or one dated before a leaving, meets nothing.
 */
export function visitMet(rows: readonly SiteVisitRow[], visitId: string): string | null {
  const met = rows.filter((d) => d.status === 'Complete' && d.met_by_property_visit_id === visitId);
  if (met.length === 0) return null;
  return met.map((d) => (d.property_left_on
    ? `the site visit owed after the client left on ${formatDate(d.property_left_on)}`
    : 'the initial site visit')).join(' and ');
}

/** The sentence after recording a visit, from the server's answer: its visit_id and the property's rows. */
export function recordedMessage(rows: readonly SiteVisitRow[], visitId: string): string {
  const met = visitMet(rows, visitId);
  return met ? `Recorded. It meets ${met}.` : 'Recorded.';
}

/** A site visit as isFirstVisit() reads it: the fields 151's definition of first uses. */
export interface VisitOrder {
  id: string;
  withdrawn: boolean;
  source: 'app' | 'load';
  /** The day of the visit, YYYY-MM-DD. */
  visited_on: string;
  /** When it was recorded (an instant). */
  created_at: string;
}

/** Whether `o` comes before `v`: an earlier visit date, or the same day recorded earlier, then by id (as 151 orders). */
function earlier(o: VisitOrder, v: VisitOrder): boolean {
  if (o.visited_on !== v.visited_on) return o.visited_on < v.visited_on;
  const a = Date.parse(o.created_at), b = Date.parse(v.created_at);
  if (a !== b) return a < b;
  return o.id < v.id;
}

/**
 * Whether a site visit is its property's FIRST, as migration 151 defines it for the Finance documents task (A-#161:
 * the EARLIEST live visit, not the only one): recorded in the app (never a load), not withdrawn, and no other live
 * visit to the property earlier, by its date and then by when it was recorded. A later visit does not stop it being
 * first. The database decides; this only chooses what the edit form says (A-#157 ruling 3). Whether a task was
 * already made for the property is not known here, so the words say "unless".
 */
export function isFirstVisit(visits: readonly VisitOrder[], visit: VisitOrder): boolean {
  return visit.source === 'app' && !visit.withdrawn
    && !visits.some((o) => o.id !== visit.id && !o.withdrawn && earlier(o, visit));
}
